/**
 * Calculates the horizontal shifts required to prevent tooltips from overlapping.
 *
 * @param {Array<{id: string, position: number, center: number, width: number}>} tooltipMeasurements
 * @param {number} containerWidth - The total width of the bounding container in pixels.
 * @param {number} [minGap=8] - The minimum allowed gap between tooltips in pixels.
 * @returns {Record<string, number>} A dictionary mapping tooltip IDs to their respective shift amounts.
 */
export const calculateOverlaps = (tooltipMeasurements, containerWidth, minGap = 8) => {
  const shifts = {};

  // Guard clause: Ensure we only process exactly two tooltips.
  if (tooltipMeasurements.length !== 2) return shifts;

  const [t1, t2] = tooltipMeasurements;

  // 1. IDENTIFY: Determine who is logically on the left and who is on the right.
  const [leftTooltip, rightTooltip] = t1.position <= t2.position ? [t1, t2] : [t2, t1];

  // The "Rigid Stick": The minimum distance allowed between the two centers.
  // [Center L] --(width/2)-- (gap) --(width/2)-- [Center R]
  const minRequiredDistance = (leftTooltip.width + rightTooltip.width) / 2 + minGap;

  // We operate on absolute coordinates (centers) during the physics calculation.
  let currentLeftCenter = leftTooltip.center;
  let currentRightCenter = rightTooltip.center;

  // ==========================================================================
  // STEP 1: RESOLVE INITIAL OVERLAP (Push apart symmetrically)
  // ==========================================================================
  // Math.max(0, ...) ensures we only get a positive overlap. If they are far apart, it's 0.
  const initialDistance = currentRightCenter - currentLeftCenter;
  const overlapAmount = Math.max(0, minRequiredDistance - initialDistance);

  // Push both tooltips away from each other by half the overlap amount.
  // <- [L]   [R] ->
  currentLeftCenter -= overlapAmount / 2;
  currentRightCenter += overlapAmount / 2;

  // ==========================================================================
  // STEP 2: LEFT BOUNDARY CONSTRAINT & RIGHTWARD CASCADE
  // ==========================================================================
  const minLeftBoundary = leftTooltip.width / 2;

  // Clamp the left tooltip so it doesn't smash through the left wall (0).
  // |[L] ->
  currentLeftCenter = Math.max(currentLeftCenter, minLeftBoundary);

  // Cascade (Chain Reaction):
  // If we just pushed [L] to the right, we must ensure [R] is pushed right as well,
  // maintaining the minimum required distance between them.
  // |[L] -> [R] ->
  currentRightCenter = Math.max(currentRightCenter, currentLeftCenter + minRequiredDistance);

  // ==========================================================================
  // STEP 3: RIGHT BOUNDARY CONSTRAINT & LEFTWARD CASCADE
  // ==========================================================================
  const maxRightBoundary = containerWidth - rightTooltip.width / 2;

  // Clamp the right tooltip so it doesn't smash through the right wall (containerWidth).
  // <- [R]|
  currentRightCenter = Math.min(currentRightCenter, maxRightBoundary);

  // Cascade (Chain Reaction):
  // If we just pushed [R] to the left, we must ensure [L] is pushed left as well,
  // preventing [R] from crushing [L].
  // <- [L] <- [R]|
  currentLeftCenter = Math.min(currentLeftCenter, currentRightCenter - minRequiredDistance);

  // ==========================================================================
  // STEP 4: CONVERT TO RELATIVE SHIFTS
  // ==========================================================================
  // We compare the final, physics-resolved absolute positions with the original
  // absolute positions to get the CSS delta (shift).
  shifts[leftTooltip.id] = currentLeftCenter - leftTooltip.center;
  shifts[rightTooltip.id] = currentRightCenter - rightTooltip.center;

  return shifts;
};
